import * as React from "react";
import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import FruitifyIcon from "./FruitifyIcons";
import Typography from "@mui/material/Typography";
import { Add } from "@mui/icons-material";
import { TagAutocomplete } from "./TagAutoComplete";

const AddTagsDialog = ({
  open,
  onClose,
  tags,
  subject,
  educationLevel,
  newTags,
  setNewTags,
  onSave,
  saving,
}) => {
  useEffect(() => {
    setNewTags([]);
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Tags</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.primary" gutterBottom>
              Current Tags
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                minHeight: "32px",
              }}
            >
              {(tags || []).length > 0 ? (
                (tags || []).map((t) => <Chip key={t.id} label={t.tag} />)
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tags yet
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
              Add New Tags
            </Typography>
            <TagAutocomplete
              value={newTags}
              onChange={setNewTags}
              subject={subject}
              educationLevel={educationLevel}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Type tags and press Enter to add each one. You can also paste a
              comma-separated list.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={saving || (newTags || []).length === 0}
        >
          Add Tags
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExpandableTagsBox = ({
  tags,
  collapsedHeight = 25,
  excludeTitle = false,
  allowAddTags = false,
  onAddClicked = () => {},
}) => {
  const [showAll, setShowAll] = useState(false);
  const [isClipped, setIsClipped] = useState(false);
  const containerRef = useRef(null);
  const theme = useTheme();

  // Check if content overflows the collapsed height
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setIsClipped(el.scrollHeight > collapsedHeight);
    }
  }, [tags, collapsedHeight]);

  return (
    <Box sx={{ mt: 2 }} data-testid={"expandable-tags-box"}>
      {/* Tags container */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.5,
          rowGap: 1,
          maxHeight: showAll ? "none" : collapsedHeight,
          overflow: "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        {/* Label */}
        {!excludeTitle && (
          <Box sx={{ display: "flex", alignItems: "center", mr: 0.5 }}>
            <FruitifyIcon
              name="Label"
              sx={{
                width: 18,
                height: 18,
                color: theme.palette.text.secondary,
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              <strong>Tags:</strong>
            </Typography>
          </Box>
        )}

        {/* Chips */}
        {Array.from(new Set(tags?.map((t) => t.tag))).map((tag) => (
          <Chip key={tag} label={tag} size="small" />
        ))}
        {allowAddTags && (
          <IconButton
            data-testid={"add-tags-icon-button"}
            size="small"
            color="success"
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                color: "primary.contrastText",
                backgroundColor: "primary.main",
                borderColor: "primary.main",
              },
            }}
            onClick={onAddClicked}
          >
            <Add fontSize="small" fill={"white"} />
          </IconButton>
        )}
      </Box>

      {/* Show All / Show Less */}
      {isClipped && (
        <Button
          variant="text"
          size="small"
          sx={{ textTransform: "none", px: 0, minWidth: 0, mt: 0.5 }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "Show less" : "Show all"}
        </Button>
      )}
    </Box>
  );
};

export { AddTagsDialog };
export default ExpandableTagsBox;
